import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Grid2 as Grid, MenuItem, TextField } from "@mui/material";
import ValidationError from "@components/ValidationError/ValidationError";
import { UseFormReturn } from "react-hook-form";
import OnboardingFormSection from "@components/Onboarding/OnboardingForm/OnboardingFormSection";
import ReactHookFormDatePicker from "@components/ReactHookFormDatePicker/ReactHookFormDatePicker";
import { PatientFormType } from "@utils/patientFormHelper";
import styled from "@emotion/styled";
import UserAvatar from "@components/UserAvatar/UserAvatar";
import ReactHookFormSelect from "@components/ReactHookFormSelect/ReactHookFormSelect";
import { Language } from "@models/language";
import CustomLabelInput from "@components/CustomLabelInput";
import GenderSelect from "@components/GenderSelect/GenderSelect";
import ResidenciesForm from "@components/ResidenciesForm";
import OccupationsForm from "@components/OccupationsForm/OccupationsForm";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PatientInfoForm: FC<{
  form: UseFormReturn<PatientFormType>;
  onAvatarChange: (image: File | undefined | null) => void;
}> = ({ form, onAvatarChange }) => {
  const { t } = useTranslation();
  const languages = Object.entries(Language).sort((l1, l2) =>
    t(l1[0], { ns: "language" })?.localeCompare(t(l2[0], { ns: "language" })),
  );

  const {
    control,
    register,
    formState: { errors },
    watch,
  } = form;

  return (
    <Container>
      <OnboardingFormSection title={t("personForm.identity")} marginTop={false}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <UserAvatar image={watch("avatarUrl")} onChange={onAvatarChange} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Grid container spacing={3}>
              <Grid size={12}>
                <CustomLabelInput
                  label={t("patient.nicknameInput.label")}
                  id="nickname"
                >
                  <TextField
                    id="nickname"
                    {...register("nickname")}
                    error={!!errors?.nickname}
                    type="text"
                    size="small"
                    variant="outlined"
                    fullWidth
                    placeholder={t("patient.nicknameInput.placeholder")}
                  />
                </CustomLabelInput>
              </Grid>
              <Grid size={12}>
                <GenderSelect
                  label={t("personForm.genderSelect.label")}
                  control={control}
                  name="gender"
                  required={true}
                  error={!!errors?.gender}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 6, md: 4 }}>
            <CustomLabelInput
              label={t("personForm.firstNameInput.label")}
              id="firstName"
              required={true}
            >
              <TextField
                {...register("firstName", { required: true })}
                error={!!errors?.firstName}
                id="firstName"
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                placeholder={t("personForm.firstNameInput.placeholder")}
              />
            </CustomLabelInput>
          </Grid>

          <Grid size={{ xs: 6, md: 4 }}>
            <CustomLabelInput
              label={t("personForm.middleNameInput.label")}
              id="middleName"
            >
              <TextField
                {...register("middleName")}
                error={!!errors?.middleName}
                id="middleName"
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                placeholder={t("personForm.middleNameInput.placeholder")}
              />
            </CustomLabelInput>
          </Grid>

          <Grid size={{ xs: 6, md: 4 }}>
            <CustomLabelInput
              label={t("personForm.lastNameInput.label")}
              id="lastName"
              required={true}
            >
              <TextField
                {...register("lastName", { required: true })}
                error={!!errors?.lastName}
                id="lastName"
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                placeholder={t("personForm.lastNameInput.placeholder")}
              />
            </CustomLabelInput>
          </Grid>

          <Grid size={{ xs: 6, md: 4 }}>
            <ReactHookFormSelect
              size="small"
              label={t("patient.languageInput.label")}
              placeholder={t("patient.languageInput.placeholder")}
              displayValueFunction={(v: string) => t(v, { ns: "language" })}
              fullWidth={true}
              controller={{
                control,
                rules: { required: true },
                name: "language",
                defaultValue: "",
              }}
              error={!!errors?.language}
              required={true}
            >
              {languages.map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {t(key, { ns: "language" })}
                </MenuItem>
              ))}
            </ReactHookFormSelect>
          </Grid>
        </Grid>
      </OnboardingFormSection>

      <OnboardingFormSection title={t("personForm.birth")}>
        <Grid container spacing={2}>
          <Grid size={6}>
            <CustomLabelInput
              label={t("personForm.birthDateInput.label")}
              id="birthDate"
            >
              <ReactHookFormDatePicker
                slotProps={{
                  textField: { size: "small", error: !!errors?.birthDate },
                }}
                id="birthDate"
                controller={{
                  name: "birthDate",
                  control,
                  defaultValue: null,
                }}
              />
            </CustomLabelInput>
          </Grid>

          <Grid size={6}>
            <CustomLabelInput
              label={t("personForm.birthLocationInput.label")}
              id="birthLocation"
            >
              <TextField
                {...register("birthLocation")}
                error={!!errors?.birthLocation}
                id="birthLocation"
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                placeholder={t("personForm.birthLocationInput.placeholder")}
              />
            </CustomLabelInput>
          </Grid>
        </Grid>
      </OnboardingFormSection>

      <OnboardingFormSection title={t("personForm.about")}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <CustomLabelInput
              label={t("personForm.bioInput.label")}
              id="bio"
            >
              <TextField
                {...register("bio")}
                error={!!errors?.bio}
                id="bio"
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                multiline
                minRows={3}
                placeholder={t("personForm.bioInput.placeholder")}
              />
            </CustomLabelInput>
          </Grid>

          <Grid size={12}>
            <CustomLabelInput
              label={t("personForm.hobbiesInput.label")}
              id="hobbies"
            >
              <TextField
                {...register("hobbies")}
                error={!!errors?.hobbies}
                id="hobbies"
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                placeholder={t("personForm.hobbiesInput.placeholder")}
              />
            </CustomLabelInput>
          </Grid>
        </Grid>
      </OnboardingFormSection>

      <OnboardingFormSection title={t("personForm.residencies")}>
        <ResidenciesForm form={form} />
      </OnboardingFormSection>

      <OnboardingFormSection title={t("personForm.occupations")}>
        <OccupationsForm form={form} />
      </OnboardingFormSection>

      {errors?.root && (
        <ValidationError>{t(`errors.${errors.root.type}`)}</ValidationError>
      )}
    </Container>
  );
};

export default PatientInfoForm;
